import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogoMainHorizontal } from '@/components/brand';
import { ArrowLeft, ArrowUp, Printer } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { SignInPage } from '../constants';

export type LegalSection = {
    id: string;
    title: string;
    children: ReactNode;
};

type LegalDocumentPageProps = {
    title: string;
    description: string;
    updatedAt: string;
    version: string;
    sections: LegalSection[];
    notice?: ReactNode;
};

export default function LegalDocumentPage({
    title,
    description,
    updatedAt,
    version,
    sections,
    notice,
}: LegalDocumentPageProps) {
    const [showBackToTop, setShowBackToTop] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const returnTo = (() => {
        const state = location.state as { returnTo?: unknown } | null;
        return typeof state?.returnTo === 'string' && state.returnTo ? state.returnTo : null;
    })();

    const handleBack = () => {
        navigate(returnTo ?? `/${SignInPage}`);
    };

    useEffect(() => {
        document.title = `LaudoPsi | ${title}`;

        const handleScroll = () => {
            setShowBackToTop(window.scrollY > 480);
        };

        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [title]);

    return (
        <main className="min-h-screen bg-background text-foreground print:bg-white">
            <div className="mx-auto flex w-full max-w-5xl flex-col px-4 py-6 sm:px-6 lg:px-8 lg:py-10 print:max-w-none print:p-0">
                <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between print:hidden">
                    <Link to="/" className="w-fit" aria-label="Ir para o LaudoPsi">
                        <LogoMainHorizontal />
                    </Link>

                    <div className="flex flex-wrap items-center gap-2">
                        <Button type="button" variant="outline" onClick={handleBack}>
                            <ArrowLeft className="size-4" />
                            Voltar
                        </Button>
                        <Button type="button" variant="outline" onClick={() => window.print()}>
                            <Printer className="size-4" />
                            Salvar PDF
                        </Button>
                    </div>
                </header>

                <article className="rounded-2xl border border-border bg-card p-5 shadow-elevation-1 sm:p-8 lg:p-10 print:border-0 print:p-0 print:shadow-none">
                    <section className="border-b border-border pb-8">
                        <h1 className="max-w-3xl text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                            {title}
                        </h1>
                        <p className="mt-4 max-w-3xl text-base leading-7 text-muted-foreground">
                            {description}
                        </p>
                        <dl className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
                            <div className="flex gap-1.5">
                                <dt>Atualização:</dt>
                                <dd className="font-medium text-foreground">{updatedAt}</dd>
                            </div>
                            <div className="flex gap-1.5">
                                <dt>Versão:</dt>
                                <dd className="font-medium text-foreground">{version}</dd>
                            </div>
                        </dl>
                    </section>

                    {notice && (
                        <section className="mt-6 rounded-xl border border-border bg-surface-muted p-4 text-sm leading-7 text-muted-foreground">
                            {notice}
                        </section>
                    )}

                    <div className="grid gap-10 pt-8 lg:grid-cols-[240px_minmax(0,1fr)]">
                        <aside className="hidden lg:block print:hidden">
                            <nav className="sticky top-6" aria-label="Seções do documento">
                                <p className="mb-3 text-sm font-semibold text-foreground">
                                    Nesta página
                                </p>
                                <div className="space-y-1">
                                    {sections.map((section, index) => (
                                        <a
                                            key={section.id}
                                            href={`#${section.id}`}
                                            className="block rounded-lg px-3 py-2 text-sm leading-5 text-muted-foreground transition hover:bg-surface-muted hover:text-foreground"
                                        >
                                            {index + 1}. {section.title}
                                        </a>
                                    ))}
                                </div>
                            </nav>
                        </aside>

                        <div className="space-y-10">
                            {sections.map((section, index) => (
                                <section
                                    key={section.id}
                                    id={section.id}
                                    className="scroll-mt-6 border-b border-border pb-10 last:border-b-0 last:pb-0"
                                >
                                    <div className="mb-4 flex items-start gap-3">
                                        <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg bg-brand-subtle text-xs font-semibold text-brand">
                                            {index + 1}
                                        </span>
                                        <h2 className="text-xl font-bold tracking-tight text-foreground sm:text-2xl">
                                            {section.title}
                                        </h2>
                                    </div>
                                    <div className="prose prose-slate max-w-none prose-p:leading-8 prose-a:font-semibold prose-a:text-brand prose-strong:text-foreground prose-li:leading-7">
                                        {section.children}
                                    </div>
                                </section>
                            ))}
                        </div>
                    </div>

                    <footer className="mt-10 border-t border-border pt-6 text-sm leading-7 text-muted-foreground">
                        <p className="m-0">
                            <span className="font-medium text-foreground">
                                NAPOLI SOFTWARE DEVELOPMENT LTDA
                            </span>
                            {' · '}CNPJ 55.764.444/0001-95{' · '}
                            contato@laudopsi.com
                        </p>
                    </footer>
                </article>
            </div>

            {showBackToTop && (
                <Button
                    type="button"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="fixed bottom-6 right-6 z-50 rounded-full shadow-elevation-2 print:hidden"
                    aria-label="Voltar ao topo"
                >
                    <ArrowUp className="size-4" />
                    Topo
                </Button>
            )}
        </main>
    );
}
